'use strict';
import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { LangContext } from '../App';
import ChasseHuntMap from '../components/ChasseHuntMap';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

const T = {
  fr: {
    title: 'Mes chasses personnelles',
    sousTitre: (credits, maxPub) =>
      `Journal privé de vos sorties. Chaque capture vous rapporte ${credits} crédit${credits > 1 ? 's' : ''}. ` +
      `Les comptes Standard sont limités à ${maxPub} capture${maxPub > 1 ? 's' : ''} publique${maxPub > 1 ? 's' : ''}.`,
    ajouterPhoto: 'Ajouter des captures',
    photos: 'Photos',
    photosDropzone: 'Glisser-déposer ou cliquer pour ajouter',
    photosFormats: "JPEG, PNG, WebP, HEIC — jusqu'à 10 photos, 10 Mo chacune",
    photosSelect: (n) => `${n} capture${n > 1 ? 's' : ''} sélectionnée${n > 1 ? 's' : ''}`,
    species: 'Espèce (optionnel)',
    especePlaceholder: 'ex. Cerf, Orignal, Perdrix…',
    typeActivite: "Type d'activité",
    typesActivite: {
      chasse: 'Chasse',
      piegeage: 'Piégeage',
      autre: 'Autre',
    },
    horodatage: 'Date et heure de la capture',
    visibility: 'Visibilité',
    privee: 'Privée',
    publique: 'Publique',
    geoloc: 'Position GPS (optionnel)',
    geolocLat: 'Latitude',
    geolocLng: 'Longitude',
    geolocBtn: '📍 Utiliser ma position',
    geolocCarte: '🗺️ Choisir sur la carte',
    geolocCarteFermer: 'Fermer la carte',
    geolocCarteAide: 'Cliquez sur la carte ou déplacez le marqueur pour définir la position.',
    geolocEnCours: 'Localisation…',
    geolocNonDispo: 'Géolocalisation non disponible sur cet appareil.',
    geolocRefuse: 'Position GPS refusée ou indisponible.',
    soumettre: (n) => `Ajouter ${n} capture${n > 1 ? 's' : ''}`,
    soumettreVide: 'Ajouter',
    enCours: 'Téléversement en cours…',
    champRequis: '(requis)',
    succes: (n, credits) => {
      const total = n * credits;
      return `${n} capture${n > 1 ? 's' : ''} ajoutée${n > 1 ? 's' : ''} ! +${total} crédit${total > 1 ? 's' : ''}`;
    },
    erreurRequis: 'Veuillez sélectionner au moins une capture et indiquer la date.',
    erreurGeoloc: 'Position GPS introuvable. Autorisez la géolocalisation ou réessayez.',
    // Backend renvoie un code stable + meta (ex: { max: 5 }) ; on traduit
    // ici pour rester FR/EN-aware. Si le code n'est pas mappé, on retombe
    // sur err.error (texte FR brut).
    erreursParCode: {
      HUNT_DATE_INVALID: () =>
        'La date de la capture est invalide. Veuillez la sélectionner à nouveau.',
      HUNT_DATE_FUTURE: () =>
        "La date de la capture ne peut pas être dans le futur. Vérifiez l'heure de votre appareil.",
      HUNT_DATE_TOO_OLD: () =>
        'Cette capture est trop ancienne (plus de 60 jours). Veuillez en ajouter une plus récente.',
      HUNT_DAILY_QUOTA: (m) => `Quota journalier atteint (${m?.max ?? 5} captures/jour).`,
      HUNT_PUBLIC_LIMIT_STANDARD: (m) =>
        `Limite de ${m?.max ?? 10} captures publiques atteinte pour un compte Standard. ` +
        'Passez à Premium pour des captures publiques illimitées.',
    },
    aucuneChasse: "Aucune capture enregistrée pour l'instant.",
    chargement: 'Chargement…',
    erreurChargement: 'Erreur lors du chargement des chasses.',
    precedent: '← Précédent',
    suivant: 'Suivant →',
    page: 'Page',
    changerVisibilite: 'Changer la visibilité',
    rendrPublique: 'Rendre publique',
    rendrPrivee: 'Rendre privée',
    erreurVisibilite: 'Erreur lors du changement de visibilité.',
    badgePrivee: 'Privée',
    badgePublique: 'Publique',
  },
  en: {
    title: 'My Personal Hunts',
    sousTitre: (credits, maxPub) =>
      `Private log of your outings. Each harvest earns ${credits} credit${credits > 1 ? 's' : ''}. ` +
      `Standard accounts are limited to ${maxPub} public harvest${maxPub > 1 ? 's' : ''}.`,
    ajouterPhoto: 'Add Harvests',
    photos: 'Photos',
    photosDropzone: 'Drag-and-drop or click to add',
    photosFormats: 'JPEG, PNG, WebP, HEIC — up to 10 photos, 10 MB each',
    photosSelect: (n) => `${n} harvest${n > 1 ? 's' : ''} selected`,
    species: 'Species (optional)',
    especePlaceholder: 'e.g. Deer, Moose, Partridge…',
    typeActivite: 'Activity type',
    typesActivite: {
      chasse: 'Hunting',
      piegeage: 'Trapping',
      autre: 'Other',
    },
    horodatage: 'Harvest date and time',
    visibility: 'Visibility',
    privee: 'Private',
    publique: 'Public',
    geoloc: 'GPS position (optional)',
    geolocLat: 'Latitude',
    geolocLng: 'Longitude',
    geolocBtn: '📍 Use my position',
    geolocCarte: '🗺️ Pick on map',
    geolocCarteFermer: 'Close map',
    geolocCarteAide: 'Click the map or drag the marker to set the position.',
    geolocEnCours: 'Locating…',
    geolocNonDispo: 'Geolocation not available on this device.',
    geolocRefuse: 'GPS position denied or unavailable.',
    soumettre: (n) => `Add ${n} harvest${n > 1 ? 's' : ''}`,
    soumettreVide: 'Add',
    enCours: 'Uploading…',
    champRequis: '(required)',
    succes: (n, credits) => {
      const total = n * credits;
      return `${n} harvest${n > 1 ? 's' : ''} added! +${total} credit${total > 1 ? 's' : ''}`;
    },
    erreurRequis: 'Please select at least one harvest and provide the date.',
    erreurGeoloc: 'GPS position unavailable. Please allow location access or try again.',
    erreursParCode: {
      HUNT_DATE_INVALID: () => 'The harvest date is invalid. Please pick it again.',
      HUNT_DATE_FUTURE: () => "The harvest date can't be in the future. Check your device's clock.",
      HUNT_DATE_TOO_OLD: () =>
        'This harvest is too old (more than 60 days). Please add a more recent one.',
      HUNT_DAILY_QUOTA: (m) => `Daily quota reached (${m?.max ?? 5} harvests/day).`,
      HUNT_PUBLIC_LIMIT_STANDARD: (m) =>
        `Limit of ${m?.max ?? 10} public harvests reached on a Standard account. ` +
        'Upgrade to Premium for unlimited public harvests.',
    },
    aucuneChasse: 'No harvests recorded yet.',
    chargement: 'Loading…',
    erreurChargement: 'Error loading hunts.',
    precedent: '← Previous',
    suivant: 'Next →',
    page: 'Page',
    changerVisibilite: 'Change visibility',
    rendrPublique: 'Make public',
    rendrPrivee: 'Make private',
    erreurVisibilite: 'Error changing visibility.',
    badgePrivee: 'Private',
    badgePublique: 'Public',
  },
};

const styles = {
  inner: {
    maxWidth: 860,
    margin: '0 auto',
    paddingBottom: '4rem',
  },
  title: {
    fontSize: '1.7rem',
    fontWeight: 700,
    marginBottom: '0.4rem',
  },
  sousTitre: {
    color: 'var(--couleur-texte-attenué)',
    marginBottom: '2rem',
    fontSize: '0.95rem',
  },
  section: {
    background: 'var(--couleur-fond)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 'var(--rayon)',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  sectionTitre: {
    fontSize: '1.1rem',
    fontWeight: 700,
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontWeight: 600,
    marginBottom: '0.35rem',
    fontSize: '0.88rem',
  },
  requis: {
    color: 'var(--couleur-or)',
    fontWeight: 400,
    fontSize: '0.78rem',
    marginLeft: '0.4rem',
  },
  input: {
    width: '100%',
    padding: '0.55rem 0.75rem',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    color: 'var(--couleur-texte)',
    fontSize: '0.92rem',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '0.55rem 0.75rem',
    background: '#1a2535',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    color: 'var(--couleur-texte)',
    fontSize: '0.92rem',
    boxSizing: 'border-box',
  },
  rangee: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  },
  dropzone: {
    border: '2px dashed rgba(255,255,255,0.18)',
    borderRadius: 8,
    padding: '1.4rem',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s, color 0.15s',
    color: 'rgba(255,255,255,0.55)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.3rem',
    minHeight: 120,
  },
  dropzoneActive: {
    borderColor: 'var(--couleur-or)',
    background: 'rgba(201,162,39,0.05)',
    color: 'var(--couleur-texte)',
  },
  thumbGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
    gap: '0.55rem',
    marginTop: '0.75rem',
  },
  thumb: {
    position: 'relative',
    aspectRatio: '4 / 3',
    borderRadius: 6,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.12)',
    background: '#000',
  },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  thumbRemove: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: 'rgba(0,0,0,0.7)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.4)',
    fontSize: 12,
    cursor: 'pointer',
    lineHeight: 1,
  },
  champ: {
    marginBottom: '1rem',
  },
  btnAjouter: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--couleur-or)',
    color: '#000',
    border: 'none',
    borderRadius: 8,
    fontWeight: 700,
    fontSize: '0.95rem',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  erreur: {
    background: 'rgba(220,38,38,0.12)',
    border: '1px solid rgba(220,38,38,0.4)',
    borderRadius: 8,
    padding: '0.7rem 1rem',
    color: 'var(--couleur-texte-erreur)',
    marginBottom: '0.8rem',
    fontSize: '0.88rem',
  },
  succes: {
    background: 'rgba(34,197,94,0.12)',
    border: '1px solid rgba(34,197,94,0.4)',
    borderRadius: 8,
    padding: '0.7rem 1rem',
    color: 'var(--couleur-texte-succes)',
    marginBottom: '0.8rem',
    fontSize: '0.88rem',
  },
  grille: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '1rem',
  },
  carte: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    overflow: 'hidden',
  },
  carteImg: {
    width: '100%',
    height: 140,
    objectFit: 'cover',
    background: 'rgba(255,255,255,0.06)',
    display: 'block',
  },
  carteCorps: {
    padding: '0.6rem 0.75rem',
  },
  carteEspece: {
    fontWeight: 600,
    fontSize: '0.88rem',
    marginBottom: '0.2rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  carteDate: {
    fontSize: '0.75rem',
    color: 'var(--couleur-texte-attenué)',
    marginBottom: '0.5rem',
  },
  badge: {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: 4,
    fontSize: '0.72rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
  },
  badgePrivee: {
    background: 'rgba(100,116,139,0.3)',
    color: '#94a3b8',
  },
  badgePublique: {
    background: 'rgba(34,197,94,0.2)',
    color: '#4ade80',
  },
  btnVis: {
    width: '100%',
    padding: '0.35rem 0.5rem',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 5,
    color: 'var(--couleur-texte)',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  btnPage: {
    padding: '0.4rem 0.9rem',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    color: 'var(--couleur-texte)',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  btnPageDisabled: {
    opacity: 0.4,
    cursor: 'default',
  },
};

function defaultHorodatage() {
  const d = new Date(Date.now() - 5 * 60 * 1000);
  return d.toISOString().slice(0, 16);
}

export default function MesChasses() {
  const { lang } = useContext(LangContext);
  const t = T[lang];

  // Form state — `fichiers` holds the File objects the user picked plus a
  // local object-URL preview so we can render thumbnails without re-reading
  // each file. Object URLs are revoked on remove and on submit-success.
  const [fichiers, setFichiers] = useState([]); // [{ file, preview }]
  const [dragHover, setDragHover] = useState(false);
  const fileInputRef = useRef(null);
  const [species, setEspece] = useState('');
  const [typeActivite, setTypeActivite] = useState('');
  const [horodatage, setHorodatage] = useState(defaultHorodatage);
  const [visibility, setVisibilite] = useState('privee');
  // Geoloc is optional. Stored as two text inputs so the user can paste
  // coords manually or fill them via "📍 Use current position".
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoErreur, setGeoErreur] = useState('');
  const [carteOuverte, setCarteOuverte] = useState(false);
  const [formEnCours, setFormEnCours] = useState(false);
  const [formSucces, setFormSucces] = useState('');
  const [formErreur, setFormErreur] = useState('');

  // List state
  const [chasses, setChasses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [listChargement, setListChargement] = useState(true);
  const [listErreur, setListErreur] = useState('');
  const [visErreurs, setVisErreurs] = useState({});

  // Public config — drives the dynamic subtitle ("Chaque capture vous
  // rapporte N crédit(s)…") so the message always matches the live
  // app_config values an admin set. Defaults match the seed values.
  const [creditsParCapture, setCreditsParCapture] = useState(1);
  const [maxPubliquesStandard, setMaxPubliquesStandard] = useState(10);

  useEffect(() => {
    let annule = false;
    api
      .get('/public/config')
      .then((res) => {
        if (annule) return;
        if (Number.isFinite(res.data?.credits_chasse_personnelle_privee)) {
          setCreditsParCapture(res.data.credits_chasse_personnelle_privee);
        }
        if (Number.isFinite(res.data?.credits_chasse_publique_max_standard)) {
          setMaxPubliquesStandard(res.data.credits_chasse_publique_max_standard);
        }
      })
      .catch(() => {
        /* keep defaults */
      });
    return () => {
      annule = true;
    };
  }, []);

  const annule = useRef(false);
  const LIMITE = 12;

  useEffect(
    () => () => {
      annule.current = true;
    },
    []
  );

  const chargerChasses = useCallback(
    async (p) => {
      setListChargement(true);
      setListErreur('');
      try {
        const res = await api.get('/me/chasses-personnelles', {
          params: { page: p, limite: LIMITE },
        });
        if (!annule.current) {
          setChasses(res.data.data || res.data.chasses || []);
          setTotal(res.data.total || 0);
        }
      } catch {
        if (!annule.current) setListErreur(t.erreurChargement);
      } finally {
        if (!annule.current) setListChargement(false);
      }
    },
    [t.erreurChargement]
  );

  useEffect(() => {
    chargerChasses(page);
  }, [page, chargerChasses]);

  // Merge incoming File list with the current selection (cap at 10).
  function ajouterFichiers(fileList) {
    const next = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (next.length === 0) return;
    setFichiers((prev) => {
      const place = Math.max(0, 10 - prev.length);
      const accepted = next.slice(0, place).map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      return [...prev, ...accepted];
    });
  }

  function retirerFichier(i) {
    setFichiers((prev) => {
      const copie = prev.slice();
      const [removed] = copie.splice(i, 1);
      if (removed) URL.revokeObjectURL(removed.preview);
      return copie;
    });
  }

  // Revoke any pending object URLs when the page unmounts.
  useEffect(
    () => () => {
      fichiers.forEach((f) => URL.revokeObjectURL(f.preview));
    },
    []
  );

  // "📍 Use my current position" — asks the browser for GPS once and writes
  // the result into the lat/lng text inputs. The user can still adjust the
  // numbers manually or clear them; geoloc is optional on the backend.
  function remplirGeolocCourante() {
    setGeoErreur('');
    if (!navigator.geolocation) {
      setGeoErreur(t.geolocNonDispo);
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        setGeoLoading(false);
      },
      () => {
        setGeoErreur(t.geolocRefuse);
        setGeoLoading(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 }
    );
  }

  async function ajouterPhoto(e) {
    e.preventDefault();
    setFormErreur('');
    setFormSucces('');

    if (fichiers.length === 0 || !horodatage) {
      setFormErreur(t.erreurRequis);
      return;
    }

    setFormEnCours(true);
    try {
      const fd = new FormData();
      fichiers.forEach(({ file }) => fd.append('photos', file));
      fd.append('photo_taken_at', new Date(horodatage).toISOString());
      if (species.trim()) fd.append('species', species.trim());
      if (typeActivite) fd.append('activity_type', typeActivite);
      fd.append('visibility', visibility);
      // Forward geoloc only when both numbers parse. Normalize French comma
      // decimal separator ("46,34534") to a period so parseFloat doesn't
      // truncate ("46,34534" → parseFloat → 46). The backend silently ignores
      // non-finite values too, but skipping the fields keeps the payload
      // small.
      const latNum = parseFloat(String(lat).replace(',', '.'));
      const lngNum = parseFloat(String(lng).replace(',', '.'));
      if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
        fd.append('photo_geoloc_lat', String(latNum));
        fd.append('photo_geoloc_lng', String(lngNum));
      }

      const res = await api.post('/me/chasses-personnelles/upload', fd, {
        // Let the browser set Content-Type with the multipart boundary —
        // forcing 'multipart/form-data' here would override that and the
        // server's multer parser can't decode the body without a boundary.
        // Override the default 'application/json' from api.js explicitly.
        headers: { 'Content-Type': undefined },
        // Multipart uploads can sit silently for minutes on a flaky link.
        // Cap at 60s so the button doesn't stay stuck on "Téléversement en
        // cours…" forever — the user gets a timeout error and can retry.
        timeout: 60_000,
      });
      if (!annule.current) {
        const n = res.data?.created?.length || fichiers.length;
        setFormSucces(t.succes(n, creditsParCapture));
        // Clear selection (revoking object URLs first to free memory).
        fichiers.forEach((f) => URL.revokeObjectURL(f.preview));
        setFichiers([]);
        setEspece('');
        setTypeActivite('');
        setHorodatage(defaultHorodatage());
        setVisibilite('privee');
        setLat('');
        setLng('');
        chargerChasses(1);
        setPage(1);
      }
    } catch (err) {
      if (!annule.current) {
        // Prefer a localized message keyed by the backend's stable `code`
        // (e.g. HUNT_DAILY_QUOTA) so EN users see "Daily quota reached…"
        // instead of the French fallback. Falls back to err.error if the
        // code isn't in the lookup or absent (older deploys).
        const data = err?.response?.data || {};
        const localized =
          data.code && t.erreursParCode?.[data.code]
            ? t.erreursParCode[data.code](data.meta || {})
            : null;
        const msg = localized || data.error || data.message || err.message;
        setFormErreur(msg);
      }
    } finally {
      if (!annule.current) setFormEnCours(false);
    }
  }

  async function changerVisibilite(chasse) {
    const nouvelleVis = chasse.visibility === 'privee' ? 'publique' : 'privee';
    setVisErreurs((prev) => ({ ...prev, [chasse.id]: '' }));
    try {
      await api.put(`/me/chasses-personnelles/${chasse.id}/visibility`, {
        visibility: nouvelleVis,
      });
      setChasses((prev) =>
        prev.map((c) => (c.id === chasse.id ? { ...c, visibility: nouvelleVis } : c))
      );
    } catch (err) {
      const msg = err?.response?.data?.error || t.erreurVisibilite;
      setVisErreurs((prev) => ({ ...prev, [chasse.id]: msg }));
    }
  }

  const totalPages = Math.ceil(total / LIMITE);

  return (
    <PrivateLayout>
      <div style={styles.inner}>
        <PageHeader
          icon="🎯"
          title={t.title}
          subtitle={t.sousTitre(creditsParCapture, maxPubliquesStandard)}
        />

        {/* Liste */}
        <div style={styles.section}>
          {listChargement && (
            <p style={{ color: 'var(--couleur-texte-attenué)' }}>{t.chargement}</p>
          )}
          {listErreur && <div style={styles.erreur}>{listErreur}</div>}

          {!listChargement && !listErreur && chasses.length === 0 && (
            <p style={{ color: 'var(--couleur-texte-attenué)', textAlign: 'center' }}>
              {t.aucuneChasse}
            </p>
          )}

          {chasses.length > 0 && (
            <>
              <div style={styles.grille}>
                {chasses.map((chasse) => (
                  <div key={chasse.id} style={styles.carte}>
                    <img
                      src={chasse.photo_url}
                      alt={chasse.species || ''}
                      style={styles.carteImg}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <div style={styles.carteCorps}>
                      {chasse.species && <div style={styles.carteEspece}>{chasse.species}</div>}
                      <div style={styles.carteDate}>
                        {chasse.photo_taken_at
                          ? new Date(chasse.photo_taken_at).toLocaleDateString(
                              lang === 'fr' ? 'fr-CA' : 'en-CA'
                            )
                          : ''}
                      </div>
                      <div>
                        <span
                          style={{
                            ...styles.badge,
                            ...(chasse.visibility === 'publique'
                              ? styles.badgePublique
                              : styles.badgePrivee),
                          }}
                        >
                          {chasse.visibility === 'publique' ? t.badgePublique : t.badgePrivee}
                        </span>
                      </div>
                      <button style={styles.btnVis} onClick={() => changerVisibilite(chasse)}>
                        {chasse.visibility === 'privee' ? t.rendrPublique : t.rendrPrivee}
                      </button>
                      {visErreurs[chasse.id] && (
                        <p
                          style={{
                            color: 'var(--couleur-texte-erreur)',
                            fontSize: '0.72rem',
                            marginTop: '0.3rem',
                          }}
                        >
                          {visErreurs[chasse.id]}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div style={styles.pagination}>
                  <button
                    style={{ ...styles.btnPage, ...(page <= 1 ? styles.btnPageDisabled : {}) }}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    {t.precedent}
                  </button>
                  <span style={{ fontSize: '0.85rem', color: 'var(--couleur-texte-attenué)' }}>
                    {t.page} {page} / {totalPages}
                  </span>
                  <button
                    style={{
                      ...styles.btnPage,
                      ...(page >= totalPages ? styles.btnPageDisabled : {}),
                    }}
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    {t.suivant}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Formulaire ajout */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitre}>{t.ajouterPhoto}</h2>

          {formSucces && <div style={styles.succes}>{formSucces}</div>}
          {formErreur && <div style={styles.erreur}>{formErreur}</div>}

          <form onSubmit={ajouterPhoto} noValidate>
            <div style={styles.champ}>
              <label style={styles.label}>
                {t.photos}
                <span style={styles.requis}>{t.champRequis}</span>
              </label>
              <div
                style={{ ...styles.dropzone, ...(dragHover ? styles.dropzoneActive : null) }}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragHover(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragHover(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setDragHover(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragHover(false);
                  ajouterFichiers(e.dataTransfer?.files);
                }}
                role="button"
                tabIndex={0}
              >
                <div style={{ fontSize: '1.5rem' }}>📷</div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t.photosDropzone}</div>
                <div style={{ fontSize: '0.74rem', opacity: 0.7 }}>{t.photosFormats}</div>
                {fichiers.length > 0 && (
                  <div
                    style={{ fontSize: '0.78rem', color: 'var(--couleur-or)', marginTop: '0.3rem' }}
                  >
                    {t.photosSelect(fichiers.length)}
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  ajouterFichiers(e.target.files);
                  // Reset value so picking the same file twice in a row works.
                  e.target.value = '';
                }}
              />
              {fichiers.length > 0 && (
                <div style={styles.thumbGrid}>
                  {fichiers.map((f, i) => (
                    <div key={i} style={styles.thumb}>
                      <img src={f.preview} alt={f.file.name} style={styles.thumbImg} />
                      <button
                        type="button"
                        onClick={() => retirerFichier(i)}
                        style={styles.thumbRemove}
                        aria-label="Retirer"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.rangee}>
              <div style={styles.champ}>
                <label style={styles.label}>{t.species}</label>
                <input
                  style={styles.input}
                  type="text"
                  value={species}
                  onChange={(e) => setEspece(e.target.value)}
                  placeholder={t.especePlaceholder}
                />
              </div>
              <div style={styles.champ}>
                <label style={styles.label}>{t.typeActivite}</label>
                <select
                  style={styles.select}
                  value={typeActivite}
                  onChange={(e) => setTypeActivite(e.target.value)}
                >
                  <option value="">—</option>
                  {Object.entries(t.typesActivite).map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.rangee}>
              <div style={styles.champ}>
                <label style={styles.label}>
                  {t.horodatage}
                  <span style={styles.requis}>{t.champRequis}</span>
                </label>
                <input
                  style={styles.input}
                  type="datetime-local"
                  step="60"
                  value={horodatage}
                  onChange={(e) => setHorodatage(e.target.value)}
                  required
                />
              </div>
              <div style={styles.champ}>
                <label style={styles.label}>{t.visibility}</label>
                <select
                  style={styles.select}
                  value={visibility}
                  onChange={(e) => setVisibilite(e.target.value)}
                >
                  <option value="privee">{t.privee}</option>
                  <option value="publique">{t.publique}</option>
                </select>
              </div>
            </div>

            {/* Optional GPS position. Two manual inputs + "use my position"
              button so the user can set the location even when EXIF GPS
              is stripped (desktop / web uploads). */}
            <div style={styles.champ}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: '0.6rem',
                  flexWrap: 'wrap',
                  marginBottom: '0.35rem',
                }}
              >
                <label style={{ ...styles.label, marginBottom: 0 }}>{t.geoloc}</label>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={remplirGeolocCourante}
                    disabled={geoLoading}
                    style={{
                      background: 'transparent',
                      border: '1px solid rgba(201,162,39,0.5)',
                      color: 'var(--couleur-or)',
                      padding: '0.3rem 0.7rem',
                      borderRadius: 5,
                      fontSize: '0.78rem',
                      cursor: geoLoading ? 'not-allowed' : 'pointer',
                      opacity: geoLoading ? 0.6 : 1,
                    }}
                  >
                    {geoLoading ? t.geolocEnCours : t.geolocBtn}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCarteOuverte((o) => !o)}
                    style={{
                      background: carteOuverte ? 'rgba(201,162,39,0.15)' : 'transparent',
                      border: '1px solid rgba(201,162,39,0.5)',
                      color: 'var(--couleur-or)',
                      padding: '0.3rem 0.7rem',
                      borderRadius: 5,
                      fontSize: '0.78rem',
                      cursor: 'pointer',
                    }}
                  >
                    {carteOuverte ? t.geolocCarteFermer : t.geolocCarte}
                  </button>
                </div>
              </div>
              <div style={styles.rangee}>
                <input
                  style={styles.input}
                  type="text"
                  inputMode="decimal"
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                  placeholder={`${t.geolocLat} (ex. 46.8123)`}
                />
                <input
                  style={styles.input}
                  type="text"
                  inputMode="decimal"
                  value={lng}
                  onChange={(e) => setLng(e.target.value)}
                  placeholder={`${t.geolocLng} (ex. -71.2080)`}
                />
              </div>
              {geoErreur && (
                <p
                  style={{
                    color: 'var(--couleur-texte-erreur)',
                    fontSize: '0.78rem',
                    margin: '0.35rem 0 0',
                  }}
                >
                  {geoErreur}
                </p>
              )}

              {/* Satellite map picker. Defaults to Québec (~Québec City) so an
                empty form still shows a useful starting view; if the user
                already typed coords or pulled from GPS, MapRecenter pans
                the map to them. */}
              {carteOuverte &&
                (() => {
                  const latNum = parseFloat(String(lat).replace(',', '.'));
                  const lngNum = parseFloat(String(lng).replace(',', '.'));
                  const aCoords = Number.isFinite(latNum) && Number.isFinite(lngNum);
                  const centre = aCoords ? [latNum, lngNum] : [46.81, -71.21];
                  const zoom = aCoords ? 12 : 6;
                  const fixerCoord = (la, lo) => {
                    setLat(la.toFixed(6));
                    setLng(lo.toFixed(6));
                  };
                  return (
                    <div style={{ marginTop: '0.6rem' }}>
                      <p
                        style={{
                          fontSize: '0.78rem',
                          color: 'rgba(255,255,255,0.55)',
                          margin: '0 0 0.4rem',
                        }}
                      >
                        {t.geolocCarteAide}
                      </p>
                      <div
                        style={{
                          height: 320,
                          borderRadius: 8,
                          overflow: 'hidden',
                          border: '1px solid rgba(255,255,255,0.12)',
                        }}
                      >
                        {/* Picker GPS — clic ailleurs ou drag du marker
                            mettent à jour les champs lat/lng via fixerCoord.
                            flyToCenter recadre quand le user tape des coords
                            à la main. */}
                        <ChasseHuntMap
                          center={centre}
                          zoom={zoom}
                          basemap="satellite"
                          height="100%"
                          onClick={(latlng) => fixerCoord(latlng.lat, latlng.lng)}
                          flyToCenter={aCoords}
                          markers={
                            aCoords
                              ? [
                                  {
                                    id: 'picker',
                                    lat: latNum,
                                    lng: lngNum,
                                    draggable: true,
                                    eventHandlers: {
                                      dragend: (e) => {
                                        const p = e.target.getLatLng();
                                        fixerCoord(p.lat, p.lng);
                                      },
                                    },
                                  },
                                ]
                              : []
                          }
                        />
                      </div>
                    </div>
                  );
                })()}
            </div>

            <button
              type="submit"
              style={{ ...styles.btnAjouter, ...(formEnCours ? styles.btnDisabled : {}) }}
              disabled={formEnCours}
            >
              {formEnCours
                ? t.enCours
                : fichiers.length > 0
                  ? t.soumettre(fichiers.length)
                  : t.soumettreVide}
            </button>
          </form>
        </div>
      </div>
    </PrivateLayout>
  );
}
